/* eslint-disable */
import { useState, useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import { notificationServices } from '@/Services';
import aiPackaging from '@/Services/aiPackaging.services';
import cogsAnalyticsServices from '@/Services/Analytics Services/cogsAnalytics.services';
import { formatDate, handleResponseStreamDownload, serializeVisibilityForAmountCaption } from '@/Helpers';
import { analyticsAction } from '@/Actions';
import { useDispatch, useSelector } from 'react-redux';
import { dummyData, dummyDispatchData, dummySecondaryPackagingData } from '../DummyData/TableData';

export const useAiFmsData = (props) => {
    const { parentTab, formStore, toPodFilter } = props;
    const [cacheTime, setCacheTime] = useState(null);
    const [rawData, setRawData] = useState([]);
    const [note, setNote] = useState(null);
    const lastFetchParams = useRef({});
    const [statsSummary, setStatsSummary] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const shiftList = useSelector((state) => state?.analytics?.SHIFT_LIST);

    useEffect(() => {
        if (shiftList?.length === 0) {
            dispatch(analyticsAction?.getShifts());
        }
    }, []);

    const checkNull = useCallback((value) => {
        if ([-999, '-999', '999', 999, undefined, null, ''].includes(value)) {
            return null;
        }
        if (Number(value) === 0) {
            return null;
        }
        return Number(value);
    }, []);

    const fetchData = useCallback(async (filter, tab = parentTab, refresh = false) => {
        if (!refresh) {
            lastFetchParams.current.fetchData = { filter, tab };
        }

        if (!filter?.fromPodFilter === '' ||
            filter?.fromPodFilter === null) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'Kitchen is Mandatory',
            });
            return;
        }

        const currentTabValue = filter?.parentTab?.value ?? tab?.value;
        // const shouldUseDummyData = filter?.isPerUnit || currentTabValue === 'SECONDARY_PACKAGING' || currentTabValue.toLowerCase() === 'dispatched';


        setIsLoading(true);
        const dateRange = filter?.dateRange;
        const req = {
            data: {
                dateRangeFilter: {
                    ...(filter?.getActiveTab?.id === 3 ? { dateType: filter?.timeType?.value ?? 'ordered' } : {}),
                    from: moment(dateRange?.from).format('YYYY-MM-DD'),
                    to: moment(dateRange?.to).format('YYYY-MM-DD'),
                },
                selectedTab: filter?.parentTab?.value?.toLowerCase() ?? tab?.value?.toLowerCase(),
                selectedSubTab: filter?.tableTab?.toLowerCase(),
                selectedDataFormat: filter?.selectedTab,
                perUnit: filter?.isPerUnit,
                fromStore: filter?.fromPodFilter?.id
                    ? checkNull(filter?.fromPodFilter?.id)
                    : checkNull(formStore),
                storeId: filter?.toPodFilter?.id
                    ? checkNull(filter?.toPodFilter?.id)
                    : checkNull(toPodFilter),
                shiftId: filter?.shiftId
                    ? checkNull(filter?.shiftId)
                    : null,
                processIds: filter?.allProcessTypeMulti?.id,
                producedVariantIds: [...new Set(filter?.productWithVariant?.productData?.flatMap(item => item?.variantIds))],
                batchIds: filter?.batchIds,
                ...(refresh ? { NO_CACHE: true } : {}),
            },
        };
        // console.log("my req --> ",req,filter,tab);

        try {
            const response = await aiPackaging?.fetchFmsAnalyticsGrid(req);
            if (response?.statusCode === 200) {
                const { data, note } = response;
                setNote(note || null);
                if (response?.cacheTime) {
                    setCacheTime(response?.cacheTime);
                } else {
                    setCacheTime(null);
                }

                // Check if data is empty or null
                if (!data || (Array.isArray(data?.data) && data?.data.length === 0) ||
                    (Array.isArray(data) && data.length === 0)) {
                    console.log(currentTabValue, "Response data is empty, using dummy data");
                    // Use appropriate dummy data based on the tab
                    if (currentTabValue.toLowerCase() === 'dispatched') {
                        setRawData(dummyDispatchData?.data);
                    } else if (currentTabValue.toLowerCase() === 'secondary_packaging') {
                        setRawData(dummySecondaryPackagingData?.data);
                    } else if (filter?.isPerUnit) {
                        setRawData(dummyData?.data);
                    }
                } else {
                    if (refresh) {
                        setCacheTime('Just now');
                    }
                    setRawData(data);
                }
            } else {
                notificationServices?.generateNotification({
                    type: 'error',
                    message: response?.message,
                });
                // Use dummy data on API error as fallback
                console.log("API error, using dummy data as fallback");
                if (currentTabValue.toLowerCase() === 'dispatched') {
                    setRawData(dummyDispatchData?.data);
                } else if (currentTabValue.toLowerCase() === 'secondary_packaging') {
                    setRawData(dummySecondaryPackagingData?.data);
                } else if (filter?.isPerUnit) {
                    setRawData(dummyData?.data);
                }
            }
        } catch (error) {
            notificationServices?.generateNotification({
                type: 'error',
                message: error.message || 'Failed to fetch data',
            });
            // Use dummy data on network error as fallback
            console.log("Network error, using dummy data as fallback");
            if (currentTabValue.toLowerCase() === 'dispatched') {
                setRawData(dummyDispatchData?.data);
            } else if (currentTabValue.toLowerCase() === 'secondary_packaging') {
                setRawData(dummySecondaryPackagingData?.data);
            } else if (filter?.isPerUnit) {
                setRawData(dummyData?.data);
            }
        } finally {
            setIsLoading(false);
        }
    }, [parentTab, checkNull, formStore, toPodFilter]);

    const fetchCogsAnalysisGraph = useCallback(async (dateRange, shiftId, selectedTab, summaryType = '', refresh = false, extraFilters = {}) => {
        if (!refresh) {
            lastFetchParams.current.fetchCogsAnalysisGraph = { dateRange, shiftId, selectedTab, summaryType, extraFilters };
        }
        try {
            const apiCalls = [];
            const basePayload = {
                avoidGraph: true,
                grouping: {
                    active: true,
                    type: 'geographyWiseNew',
                    currentSelection: 'brand',
                    selection: {},
                },
                shiftId: checkNull(shiftId),
                analysisType: 'BOM',
                ...(refresh ? { NO_CACHE: true } : {}),
                ...extraFilters
            };

            const today = moment().startOf('day');
            apiCalls.push(
                cogsAnalyticsServices?.fetchCogsAnalysisGraph({
                    req: {
                        data: {
                            ...basePayload,
                            summaryType: summaryType,
                            yAxis: selectedTab,
                            dateRangeFilter: {
                                from: moment(dateRange?.from).format('YYYY-MM-DD'),
                                to: moment(dateRange?.to).format('YYYY-MM-DD'),
                            },
                        },
                    },
                })
            );

            // if (
            //     today.isBetween(
            //         moment(dateRange?.from).startOf('day'),
            //         moment(dateRange?.to).startOf('day'),
            //         null,
            //         '[]'
            //     )
            // ) {
            //     apiCalls.push(
            //         cogsAnalyticsServices?.fetchCogsAnalysisGraphToday({
            //             req: {
            //                 data: {
            //                     ...basePayload,
            //                     yAxis: selectedTab ?? 'TotalWeight',
            //                     dateRangeFilter: {
            //                         from: today.format('YYYY-MM-DD'),
            //                         to: today.format('YYYY-MM-DD'),
            //                     },
            //                 },
            //             },
            //         })
            //     );
            // }

            const [response, responseToday] = await Promise.all(apiCalls);

            if (response?.statusCode === 200) {
                console.log("resp", response);
                const mergedStatsData = response?.data?.statsData;
                // const mergedStatsData = mergeStatsData(
                //     response?.data?.statsData,
                //     responseToday?.data?.statsData
                // );
                console.log("mergedStatsDatamergedStatsData", mergedStatsData);
                const processedStats = processStatsData(mergedStatsData);
                console.log("processedStats", processedStats);
                const statsSummary = Object?.keys(processedStats)?.map((ele) => {
                    return serializeVisibilityForAmountCaption(processedStats, ele);
                });
                console.log("statsSummary", statsSummary);
                setStatsSummary(statsSummary);
            }
        } catch (error) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'Failed to fetch analytics data',
            });
        }
    }, []);

    const mergeStatsData = useCallback((historicalData, todayData) => {
        if (!historicalData) return {};
        if (!todayData) return historicalData;

        const mergedData = {
            ...historicalData,
        };

        if (Array.isArray(todayData)) {
            todayData.forEach((todayItem) => {
                const historicalKey = Object.keys(historicalData).find(
                    (key) => historicalData[key].label === todayItem.label
                );

                if (
                    historicalKey &&
                    typeof historicalData[historicalKey].value === 'number' &&
                    typeof todayItem.value === 'number'
                ) {
                    mergedData[historicalKey] = {
                        ...historicalData[historicalKey],
                        value: historicalData[historicalKey].value + todayItem.value,
                    };
                }
            });
        } else {
            Object.keys(historicalData).forEach((key) => {
                if (
                    todayData[key] &&
                    typeof historicalData[key].value === 'number' &&
                    typeof todayData[key].value === 'number'
                ) {
                    mergedData[key] = {
                        ...historicalData[key],
                        value: historicalData[key].value + todayData[key].value,
                    };
                }
            });
        }

        return mergedData;
    }, []);

    const processStatsData = useCallback((statsData) => {
        // if (!statsData) return statsData;

        // const processedData = {
        //     ...statsData,
        // };
        // const idealCostKey = 3;

        // if (!idealCostKey || !processedData[idealCostKey]?.value) {
        //     return processedData;
        // }

        // const idealCost = processedData[idealCostKey].value;

        // Object.keys(processedData).forEach((key) => {
        //     if (key !== idealCostKey && processedData[key].value && idealCost > 0) {
        //         const percentage = Math.round(
        //             (processedData[key].value / idealCost) * 100
        //         );
        //         processedData[key] = {
        //             ...processedData[key],
        //             percentageValue: percentage,
        //         };
        //     }

        // });

        return statsData;
    }, []);

    const handleExport = async (filters, tab) => {
        const fromDate = filters?.dateRange?.from;
        const toDate = filters?.dateRange?.to;
        const formattedFromDate = formatDate(fromDate);
        const formattedToDate = formatDate(toDate);
        if (formattedFromDate === '' || formattedToDate === '' || formattedFromDate === null || formattedToDate === null) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'Date is Mandatory',
            });
            return;
        }
        const req = {
            data: {
                filters: {
                    dateRangeFilter: {
                        from: formattedFromDate,
                        to: formattedToDate,
                    },
                    fromStore: filters?.fromPodFilter?.id
                        ? checkNull(filters?.fromPodFilter?.id)
                        : checkNull(formStore),
                    storeId: filters?.toPodFilter?.id
                        ? checkNull(filters?.toPodFilter?.id)
                        : checkNull(toPodFilter),
                    analysisType: tab?.value,

                },
            },
        };
        try {
            const response = await aiPackaging?.aiPacakgingExport(req);
            handleResponseStreamDownload(response, `${tab?.value} ${formattedFromDate} to ${formattedToDate}.csv`);
        } catch (error) {
            notificationServices.generateNotification({
                type: 'error',
                message: error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const refresh = useCallback(async () => {
        if (lastFetchParams.current.fetchData) {
            const { filter, tab } = lastFetchParams.current.fetchData;
            fetchData(filter, tab, true);
        }
        if (lastFetchParams.current.fetchCogsAnalysisGraph) {
            const { dateRange, shiftId, selectedTab, summaryType, } = lastFetchParams.current.fetchCogsAnalysisGraph;
            fetchCogsAnalysisGraph(dateRange, shiftId, selectedTab, summaryType, true);
        }
    }, [fetchData, fetchCogsAnalysisGraph]);

    return {
        rawData,
        statsSummary,
        isLoading,
        fetchData,
        fetchCogsAnalysisGraph,
        handleExport,
        cacheTime,
        refresh,
        note,
    };
};
